import { supabase } from '@/integrations/supabase/client';

/**
 * Read/write the current user's active-workspace pointer.
 *
 * The pointer lives on `user_profiles.active_brand_kit_id`, which is
 * USER-scoped (`RLS: auth.uid() = id`). That is the whole point: it is one of
 * the 37 tables where the raw session id is correct, so a switch is private to
 * the person who made it.
 *
 * ⚠️ Do NOT "fix" the raw `userId` here into `resolveOwnerId(userId)`. That is
 * the reflex this repo's identity invariant trains, and here it is exactly
 * wrong --- resolving would write the OWNER's pointer, which is the
 * account-wide behaviour this column was added to replace. `brand_kits` is
 * account-scoped; `user_profiles` is not. One feature, two tables, two
 * different right answers.
 */
export async function readActiveWorkspacePointer(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('active_brand_kit_id')
    .eq('id', userId)
    .maybeSingle();

  if (error) throw new Error(`Could not read active workspace: ${error.message}`);
  return data?.active_brand_kit_id ?? null;
}

/**
 * Persist the pointer. Upsert rather than update on purpose: 14 of 23 accounts
 * had no `user_profiles` row at all before 20260822190748 backfilled them and
 * added the signup trigger, and a silent no-op write is this repo's single most
 * recurring bug class. The row should always exist now; this makes it true
 * rather than assumed.
 *
 * Throws on rejection instead of swallowing it --- the DB trigger
 * `trg_user_profiles_active_kit_ownership` refuses a kit that is archived or
 * belongs to another account, and the caller must not proceed to clear caches
 * for a switch that never happened.
 */
export async function writeActiveWorkspacePointer(
  userId: string,
  brandKitId: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('user_profiles')
    .upsert({ id: userId, active_brand_kit_id: brandKitId }, { onConflict: 'id' });

  if (error) throw new Error(`Could not switch workspace: ${error.message}`);
}
